import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Divider } from '@robocorp/components';
import { IconSettingsSliders } from '@robocorp/icons/iconic';
import { usePartnerAccountsQuery } from '~/queries';
import { useIsSSO } from '~/hooks/useIsSSO';

interface Props {
  accountId: string;
  onAccountChange: (id: string) => void;
}

export const PartnerAccountMenu: FC<Props> = ({ accountId, onAccountChange }) => {
  const { t } = useTranslation();
  const { instanceId, isSSO, realm } = useIsSSO();

  const { data: partnerAccounts = [] } = usePartnerAccountsQuery();
  const currentAccount = partnerAccounts.find(({ id }) => id === accountId);
  const partnerAccountManagementLink = useMemo(() => {
    const baseLink = `${import.meta.env.VITE_ID_URL}/accounts/${accountId}/clients`;

    if (!isSSO) {
      return {
        isDisabled: false,
        href: baseLink,
      };
    }

    const isPartnerAccountAccessEnabled = instanceId === 'eu1' || instanceId === 'eu2';

    if (!isPartnerAccountAccessEnabled) {
      // Currently the partner management ONLY works for the EU1 and EU2 instance as the data is hosted there
      // As a security measure we don't even allow logging in into the partner view when the SSO is located in
      // another non-EU instance: it may otherwise create data in EU – something that should be avoided at all costs.
      return {
        isDisabled: true,
        href: baseLink,
      };
    }

    const ssoReferrer = baseLink.includes('dev')
      ? `${realm}.robocorp.dev`
      : `${realm}.robocorp.com`;

    return {
      isDisabled: false,
      href: `${baseLink}?sso-referrer=${ssoReferrer}`,
    };
  }, [accountId, instanceId, isSSO, realm]);

  if (partnerAccounts.length > 1) {
    return (
      <Menu trigger={<Menu.Item>{currentAccount?.name}</Menu.Item>}>
        <Menu.Link
          icon={<IconSettingsSliders />}
          href={`${import.meta.env.VITE_ID_URL}/accounts/${accountId}/clients`}
          target="_blank"
          role="menuitem"
        >
          {t('openPartnerAccount')}
        </Menu.Link>

        <Divider />

        {partnerAccounts.map((account) => (
          <Menu.Item
            key={account.id}
            onClick={() => onAccountChange(account.id)}
            aria-selected={account.id === accountId}
          >
            {account.name}
          </Menu.Item>
        ))}
      </Menu>
    );
  }

  return partnerAccountManagementLink.isDisabled ? (
    <Menu.Item disabled={partnerAccountManagementLink.isDisabled} icon={IconSettingsSliders}>
      {currentAccount?.name || t('openPartnerAccount')}
    </Menu.Item>
  ) : (
    <Menu.Link icon={IconSettingsSliders} href={partnerAccountManagementLink.href} target="_blank">
      {currentAccount?.name || t('openPartnerAccount')}
    </Menu.Link>
  );
};
